import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { History, Plus, Save, Pencil, Printer, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { enrollmentApi } from "@/lib/setupApi";

// Year-wise enrollment manager. Per-FY snapshot with audit trail.
// Flows downstream into Monthly MDCF auto-fill (the FY for the selected month
// is matched and overrides school-profile enrolment numbers).

const ALL_BUCKETS = [
  { key: "balVatika", label: "Bal Vatika" },
  { key: "primary", label: "Primary (I-V)" },
  { key: "upperPrimary", label: "Upper Primary (VI-VIII)" },
  { key: "highSchool", label: "High School (IX-X)" },
];

const NUM = (v) => Number(v) || 0;

function fyOptions(span = 6) {
  const now = new Date();
  const start = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  const out = [];
  for (let i = -2; i < span; i++) {
    const y = start + i;
    out.push(`${y}-${String((y + 1) % 100).padStart(2, "0")}`);
  }
  return out.reverse();
}

function emptyRow(fy) {
  return { financialYear: fy, balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0, notes: "" };
}

export default function EnrollmentManager({ schoolId, schoolName, visibleClasses, category }) {
  // Mirror the same class filter used in the school-details enrolment grid.
  // If a school is BV+Primary, year-wise enrolment only shows Bal Vatika and
  // Primary buckets; etc. Falls back to all four buckets when not supplied.
  const BUCKETS = useMemo(() => {
    if (!Array.isArray(visibleClasses) || visibleClasses.length === 0) return ALL_BUCKETS;
    const want = new Set(visibleClasses);
    return ALL_BUCKETS.filter((b) => want.has(b.key));
  }, [visibleClasses]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited (or new)
  const [historyFy, setHistoryFy] = useState(null);
  const [history, setHistory] = useState([]);
  const fyChoices = useMemo(() => fyOptions(), []);

  const refresh = async () => {
    if (!schoolId) { setRows([]); return; }
    setLoading(true);
    try {
      const list = await enrollmentApi.list(schoolId);
      setRows(list || []);
    } catch (e) { toast.error("Failed to load enrolment"); console.error(e); }
    setLoading(false);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [schoolId]);

  const startEdit = (row) => setEditing({ ...row });
  const startNew = () => {
    const used = new Set(rows.map((r) => r.financialYear));
    const firstFree = fyChoices.find((f) => !used.has(f)) || fyChoices[0];
    setEditing(emptyRow(firstFree));
  };

  const handleSave = async () => {
    if (!schoolId || !editing) return;
    if (!editing.financialYear) { toast.error("Pick a Financial Year"); return; }
    const payload = {
      schoolId,
      financialYear: editing.financialYear,
      balVatika: NUM(editing.balVatika),
      primary: NUM(editing.primary),
      upperPrimary: NUM(editing.upperPrimary),
      highSchool: NUM(editing.highSchool),
      notes: editing.notes || "",
      updatedBy: "",
    };
    try {
      await enrollmentApi.upsert(schoolId, editing.financialYear, payload);
      toast.success(`Enrolment for FY ${editing.financialYear} saved`);
      setEditing(null);
      refresh();
    } catch (e) { toast.error("Save failed"); console.error(e); }
  };

  const handleDelete = async (fy) => {
    try {
      await enrollmentApi.remove(schoolId, fy);
      toast.success(`Removed FY ${fy}`);
      refresh();
    } catch (e) { toast.error("Delete failed"); console.error(e); }
  };

  const showHistory = async (fy) => {
    setHistoryFy(fy);
    try {
      const list = await enrollmentApi.history(schoolId, fy);
      setHistory(list || []);
    } catch (e) { toast.error("Failed to load history"); console.error(e); setHistory([]); }
  };

  // PDF snapshot: open browser print dialog with a clean A4 layout.
  const printSnapshot = (row) => {
    const total = BUCKETS.reduce((acc, b) => acc + NUM(row[b.key]), 0);
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) { toast.error("Popup blocked — allow popups to print"); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>Enrolment Snapshot ${row.financialYear}</title>
      <style>
        body{font-family:Georgia,'Times New Roman',serif;color:#1f1d1a;padding:32px;max-width:780px;margin:0 auto;}
        h1{font-size:22px;text-align:center;margin:0 0 4px;border-bottom:2px solid #1f1d1a;padding-bottom:6px;}
        h2{font-size:14px;text-align:center;font-weight:400;margin:0 0 24px;}
        table{width:100%;border-collapse:collapse;margin-top:16px;}
        th,td{border:1.5px solid #1f1d1a;padding:10px 14px;text-align:left;font-size:14px;}
        th{background:#f5efe1;font-weight:600;}
        td.num{text-align:right;font-feature-settings:"tnum";}
        .total{background:#f5efe1;font-weight:600;}
        .meta{margin-top:18px;font-size:12px;color:#5f574a;}
        .sig{margin-top:48px;display:flex;justify-content:space-between;}
        .sig div{border-top:1px solid #1f1d1a;padding-top:6px;width:32%;text-align:center;font-size:12px;}
      </style></head><body>
      <h1>Year-wise Enrolment Snapshot</h1>
      <h2>${schoolName || ""} &middot; FY ${row.financialYear}</h2>
      <table>
        <thead><tr><th>Class Bucket</th><th class="num">Enrolled</th></tr></thead>
        <tbody>
          ${BUCKETS.map((b) => `<tr><td>${b.label}</td><td class="num">${NUM(row[b.key])}</td></tr>`).join("")}
          <tr class="total"><td>Total</td><td class="num">${total}</td></tr>
        </tbody>
      </table>
      ${row.notes ? `<div class="meta"><strong>Notes:</strong> ${row.notes}</div>` : ""}
      <div class="meta">Last updated: ${new Date(row.updatedAt || Date.now()).toLocaleString()}</div>
      <div class="sig"><div>Headmaster</div><div>School Management Committee</div><div>Block Education Officer</div></div>
      </body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 200);
  };

  if (!schoolId) return <p className="text-sm text-ink-700">Pick an active school to manage year-wise enrolment.</p>;

  return (
    <div className="space-y-4" data-testid="enrollment-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-ink-900">Year-wise Enrolment</h3>
          <p className="text-xs text-ink-700">
            One snapshot per Financial Year. Auto-flows into Monthly MDCF Section 5.
            {category && <> · Showing classes for <b>{category}</b>.</>}
          </p>
        </div>
        <Button onClick={startNew} className="bg-clay hover:bg-clay/90" data-testid="enrollment-add-btn">
          <Plus className="mr-1 h-4 w-4" /> Add / Update FY
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-sand-100 text-ink-900">
            <tr>
              <th className="border border-border p-2 text-left">Financial Year</th>
              {BUCKETS.map((b) => (
                <th key={b.key} className="border border-border p-2 text-right">{b.label}</th>
              ))}
              <th className="border border-border p-2 text-right">Total</th>
              <th className="border border-border p-2 text-left">Notes</th>
              <th className="border border-border p-2 text-left">Updated</th>
              <th className="border border-border p-2 w-44 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={5 + BUCKETS.length} className="p-6 text-center text-ink-700">No enrolment records yet — click "Add / Update FY" to start.</td></tr>
            )}
            {rows.map((r) => {
              const total = BUCKETS.reduce((acc, b) => acc + NUM(r[b.key]), 0);
              return (
                <tr key={r.financialYear} className="border-t border-border hover:bg-sand-50" data-testid={`enrollment-row-${r.financialYear}`}>
                  <td className="border border-border p-2 font-semibold">{r.financialYear}</td>
                  {BUCKETS.map((b) => (
                    <td key={b.key} className="border border-border p-2 text-right">{NUM(r[b.key])}</td>
                  ))}
                  <td className="border border-border p-2 text-right font-semibold">{total}</td>
                  <td className="border border-border p-2 max-w-[220px] truncate" title={r.notes}>{r.notes || "—"}</td>
                  <td className="border border-border p-2 text-xs text-ink-700">{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : "—"}</td>
                  <td className="border border-border p-2">
                    <div className="flex flex-wrap items-center justify-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(r)} data-testid={`enrollment-edit-${r.financialYear}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => showHistory(r.financialYear)} data-testid={`enrollment-history-${r.financialYear}`}>
                        <History className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => printSnapshot(r)} data-testid={`enrollment-print-${r.financialYear}`}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.financialYear)} className="text-red-600 hover:text-red-700" data-testid={`enrollment-delete-${r.financialYear}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit / Add dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="enrollment-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.updatedAt ? "Update" : "Add"} FY {editing?.financialYear || ""}</DialogTitle>
            <DialogDescription>Headcount per class bucket. Saving creates an audit-trail entry automatically.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="block mb-1 text-ink-900">Financial Year</span>
                <select
                  className="h-9 w-full rounded-md border border-border bg-white px-3 text-sm"
                  value={editing.financialYear}
                  onChange={(e) => setEditing({ ...editing, financialYear: e.target.value })}
                  data-testid="enrollment-fy-select"
                  disabled={!!editing.updatedAt}
                >
                  {!fyChoices.includes(editing.financialYear) && (
                    <option value={editing.financialYear}>{editing.financialYear}</option>
                  )}
                  {fyChoices.map((fy) => (<option key={fy} value={fy}>{fy}</option>))}
                </select>
              </label>
              {BUCKETS.map((b) => (
                <label key={b.key} className="text-sm">
                  <span className="block mb-1 text-ink-900">{b.label}</span>
                  <Input
                    type="number"
                    min="0"
                    value={editing[b.key]}
                    onChange={(e) => setEditing({ ...editing, [b.key]: e.target.value })}
                    data-testid={`enrollment-input-${b.key}`}
                  />
                </label>
              ))}
              <label className="text-sm sm:col-span-2">
                <span className="block mb-1 text-ink-900">Notes (optional)</span>
                <Input
                  value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="e.g., Updated after April admission drive"
                  data-testid="enrollment-input-notes"
                />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}><X className="mr-1 h-4 w-4" /> Cancel</Button>
            <Button onClick={handleSave} className="bg-clay hover:bg-clay/90" data-testid="enrollment-save-btn">
              <Save className="mr-1 h-4 w-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History sheet */}
      <Sheet open={!!historyFy} onOpenChange={(o) => !o && setHistoryFy(null)}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>History — FY {historyFy}</SheetTitle>
            <SheetDescription>Every edit to this enrolment record, newest first.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-4">
            {history.length === 0 && <p className="text-sm text-ink-700">No edits recorded yet.</p>}
            {history.map((h) => (
              <div key={h.id} className="rounded-lg border border-border bg-white p-3 text-sm" data-testid={`enrollment-history-row-${h.id}`}>
                <p className="text-xs text-ink-700">{new Date(h.changedAt).toLocaleString()}</p>
                <ul className="mt-2 space-y-1">
                  {h.changes.map((c, i) => (
                    <li key={i} className="font-mono text-xs">
                      <span className="font-semibold">{c.field}</span>: <span className="text-ink-700">{String(c.from ?? "—")}</span> → <span className="text-clay">{String(c.to ?? "—")}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
